import { useEffect, useRef, useState } from "react";
import {
  Zap,
  RefreshCw,
  Sparkles,
  Mic,
  Send,
} from "lucide-react";

export default function ChatPanel({
  messages = [],
  onSendMessage,
  onClearChat,
}) {
  const [input, setInput] = useState("");
  const scrollerRef = useRef(null);

  // Auto-scroll to newest message whenever messages change
  useEffect(() => {
    const n = scrollerRef.current;
    if (!n) return;
    n.scrollTo({ top: n.scrollHeight, behavior: "smooth" });
  }, [messages?.length]);

  function handleSubmit(e) {
    e.preventDefault();
    if (!input.trim() || !onSendMessage) return;
    onSendMessage(input.trim());
    setInput("");
  }

  function handleClear() {
    if (onClearChat) onClearChat();
  }

  return (
    <div className="lg:col-span-6">
      <div className="console-card h-[calc(100vh-200px)] min-h-[400px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-neon-cyan to-neon-magenta flex items-center justify-center">
              <Zap className="w-4 h-4 text-black" />
            </div>
            <div>
              <div className="text-sm font-medium">Vox</div>
              <div className="text-xs text-soft">Ready to assist</div>
            </div>
          </div>

          <button
            onClick={handleClear}
            className="console-button text-xs px-3 py-1"
            data-testid="clear-chat-btn"
          >
            <RefreshCw className="w-3 h-3 mr-1 inline" />
            Clear
          </button>
        </div>

        {/* Messages */}
        <div className="relative overflow-hidden flex-1 p-4">
          <div ref={scrollerRef} className="h-full w-full overflow-y-auto pr-4 scroll-gutter-stable">
            <div className="space-y-4 pb-2">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-neon-cyan/20 to-neon-magenta/20 flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-neon-cyan" />
                  </div>
                  <p className="text-soft text-sm mb-2">
                    Hello. I'm Vox, your personal assistant.
                  </p>
                  <p className="text-xs text-soft/60">
                    How can I help you today?
                  </p>
                </div>
              ) : (
                messages.map((m, i) => (
                  <div
                    key={m.id || i}
                    className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                      m.role === "user"
                        ? "ml-auto bg-neon-cyan/20 border border-neon-cyan/40"
                        : "bg-white/5 border border-white/10"
                    }`}
                  >
                    {m.content}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-4 border-t border-white/10">
          <div className="flex gap-3">
            <input
              className="console-input flex-1"
              placeholder="Ask Vox anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />

            <button type="button" className="console-button px-4">
              <Mic className="w-4 h-4" />
            </button>

            <button
              type="submit"
              className="console-button px-4"
              disabled={!input.trim()}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
